import type { ExtractPropTypes, PropType, Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';
import type { Cell } from '../../types';
import { SlideShowHelper } from '../../helpers/slide-show.helper';

export const slideShowProps = {
  cells: { type: Array as PropType<Cell[]>, required: true },
};

export function useSlideShow(props: ExtractPropTypes<typeof slideShowProps>) {
  const current: Ref<number> = ref(0);
  const currentGridSlide: Ref<number> = ref(0);
  const direction: Ref<number> = ref(1);
  const transitionName: Ref<string> = ref('fade');
  const isGridView: Ref<boolean> = ref(false);
  const dragPos: Ref<number> = ref(0);
  const isSticky: Ref<boolean> = ref(false);

  const slides = computed(() => props.cells[0].nodes.length);
  const gridSlides = computed(() => Math.ceil(slides.value / 9));

  const handleObserver = (isVisible: boolean) => {
    isSticky.value = !isVisible;
  };

  const observerOptions: Ref<Record<string, any>> = ref({
    callback: handleObserver,
    throttle: 100,
    intersection: {
      threshold: 1,
    },
    throttleOptions: {
      leading: 'hidden',
    },
  });

  const slide = (dir: number) => {
    direction.value = dir;
    dir === 1 ? (transitionName.value = 'slide-next') : (transitionName.value = 'slide-prev');
    if (isGridView.value) {
      currentGridSlide.value = SlideShowHelper.updateCurrentSlide(
        currentGridSlide.value,
        dir,
        gridSlides.value
      );
    } else {
      current.value = SlideShowHelper.updateCurrentSlide(current.value, dir, slides.value);
    }
  };

  const handleToggleGridView = (value: boolean) => {
    transitionName.value = '';
    isGridView.value = value;
  };

  const handleClick = (index: number) => {
    transitionName.value = '';
    isGridView.value = false;
    current.value = index;
  };

  const currentSlide = computed(() => {
    if (isGridView.value) {
      return currentGridSlide.value;
    }
    return current.value;
  });

  const handleDragStart = (e: MouseEvent | TouchEvent) => {
    if (e instanceof MouseEvent) {
      e.preventDefault();
    }
    dragPos.value = (e as MouseEvent).clientX || (e as TouchEvent).touches[0].clientX;
  };

  const handleDragEnd = (e: MouseEvent | TouchEvent) => {
    transitionName.value = '';
    const posX = (e as MouseEvent).clientX || (e as TouchEvent).changedTouches[0].clientX;
    if (dragPos.value > posX) {
      slide(1);
    } else if (dragPos.value < posX) {
      slide(-1);
    }
    dragPos.value = 0;
  };

  return {
    current,
    currentGridSlide,
    direction,
    transitionName,
    isGridView,
    dragPos,
    isSticky,
    slides,
    gridSlides,
    handleObserver,
    observerOptions,
    slide,
    handleToggleGridView,
    handleClick,
    currentSlide,
    handleDragEnd,
    handleDragStart,
  };
}
